/**
 * useVisibilityRecheck — fire a callback when the page regains visibility
 * or window focus, while `enabled`.
 *
 * Why this exists (v2.0): Bridge AI generation is a 1–2 minute LLM run. If
 * the requesting component unmounts mid-flight (journey tab switch, route
 * change), the in-flight response is dropped while the server finishes and
 * PERSISTS the insights — leaving a remounted-too-early surface stranded in
 * its "Generate Insights" empty state even though results exist. Surfaces
 * register a light persisted-state re-check here so returning to the tab
 * self-heals instead of requiring a manual page reload (seen live
 * 2026-06-11 on the journey Bridge tab).
 *
 * Behavior:
 *  - listens for `visibilitychange` (→ visible) and window `focus`;
 *  - the two often fire together on tab return — calls within 1s collapse
 *    into one;
 *  - `enabled` and the callback are read through refs, so callers may pass
 *    fresh closures every render without re-registering listeners.
 */

import { useEffect, useRef } from 'react';

const DOUBLE_FIRE_WINDOW_MS = 1000;

export function useVisibilityRecheck(onVisible: () => void, enabled: boolean): void {
  const callbackRef = useRef(onVisible);
  callbackRef.current = onVisible;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const lastFiredRef = useRef(0);

  useEffect(() => {
    const fire = () => {
      if (!enabledRef.current) return;
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastFiredRef.current < DOUBLE_FIRE_WINDOW_MS) return;
      lastFiredRef.current = now;
      callbackRef.current();
    };
    document.addEventListener('visibilitychange', fire);
    window.addEventListener('focus', fire);
    return () => {
      document.removeEventListener('visibilitychange', fire);
      window.removeEventListener('focus', fire);
    };
  }, []);
}
